/* eslint-disable react/prop-types */
import { enUS } from "date-fns/locale";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import * as React from "react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  buttonVariant = "ghost",
  locale = enUS,
  captionLayout = "dropdown",
  formatters,
  components,
  startMonth = new Date(1900, 0),
  endMonth = new Date(2100, 11),
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      initialFocus
      locale={locale}
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn("bg-background p-3", className)}
      formatters={{
        formatMonthCaption: date =>
          date.toLocaleDateString(locale.code, {
            month: "short",
          }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),

        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),

        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between",
          defaultClassNames.nav
        ),

        month_caption: cn(
          "flex h-8 w-full items-center justify-center px-8",
          defaultClassNames.month_caption
        ),

        month: cn("flex flex-col gap-4", defaultClassNames.month),

        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-8 p-0",
          defaultClassNames.button_previous
        ),

        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-8 p-0",
          defaultClassNames.button_next
        ),

        dropdowns: cn(
          "flex items-center gap-1 text-sm font-medium",
          defaultClassNames.dropdowns
        ),

        dropdown_root: cn(
          "relative rounded-md px-2 py-1 hover:bg-accent",
          defaultClassNames.dropdown_root
        ),

        dropdown: cn(
          "absolute inset-0 opacity-0 cursor-pointer",
          defaultClassNames.dropdown
        ),

        caption_label: cn(
          "flex items-center gap-1 text-sm font-medium",
          defaultClassNames.caption_label
        ),

        table: cn(
          "w-full border-separate border-spacing-0",
          defaultClassNames.table
        ),

        weekdays: cn("flex", defaultClassNames.weekdays),

        weekday: cn(
          "flex-1 text-muted-foreground text-xs font-normal",
          defaultClassNames.weekday
        ),

        week: cn("mt-1 flex w-full", defaultClassNames.week),

        day: cn("relative p-0 text-center", defaultClassNames.day),

        range_start: cn(
          "bg-accent rounded-l-md",
          defaultClassNames.range_start
        ),

        range_middle: cn(
          "bg-accent rounded-none",
          defaultClassNames.range_middle
        ),

        range_end: cn("bg-accent rounded-r-md", defaultClassNames.range_end),

        today: cn("text-foreground", defaultClassNames.today),

        selected: cn(defaultClassNames.selected),

        outside: cn(
          "text-muted-foreground opacity-40",
          defaultClassNames.outside
        ),

        disabled: cn(
          "text-muted-foreground opacity-30",
          defaultClassNames.disabled
        ),

        hidden: cn("invisible", defaultClassNames.hidden),

        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon
                className={cn("size-7 rounded-md border p-1.5", className)}
                {...props}
              />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-7 rounded-md border p-1.5", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },

        DayButton: CalendarDayButton,

        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  const isSingle =
    modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle;

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toISOString()}
      data-single={isSingle}
      data-range-start={modifiers.range_start}
      data-range-middle={modifiers.range_middle}
      data-range-end={modifiers.range_end}
      className={cn(
        "size-8 font-normal",

        "data-[single=true]:bg-primary",
        "data-[single=true]:text-primary-foreground",

        "data-[range-start=true]:bg-primary",
        "data-[range-start=true]:text-primary-foreground",
        "data-[range-start=true]:rounded-md",

        "data-[range-end=true]:bg-primary",
        "data-[range-end=true]:text-primary-foreground",
        "data-[range-end=true]:rounded-md",

        "data-[range-middle=true]:bg-accent",
        "data-[range-middle=true]:text-accent-foreground",
        "data-[range-middle=true]:rounded-none",

        "focus-visible:ring-2",
        "focus-visible:ring-ring",

        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
